import { SurveyLayoutConfig, SurveyStepConfig } from 'ngx-esprio-shared';

/**
 * WebK-specific survey presentation (ticket 27754).
 *
 * Groups, reorders and re-labels the WebK "basic-webk" question group (id
 * `must`) purely on the frontend — no backend/survey-data change. Element ids
 * refer to the elements defined in the backend seed `basicWebKQuestion`, and
 * answer *values* are never touched, so scoring is unaffected.
 *
 * Region icons come from `assets/images/Symptom*.png`; the `icon` emoji next to
 * each one is the fallback shown if an image is missing.
 *
 * The sections below are shared by two alternative presentations:
 * - {@link WEBK_SURVEY_LAYOUT}          — one step per section (four screens).
 * - {@link WEBK_SURVEY_LAYOUT_ONE_PAGE} — all sections on one scrollable page.
 *
 * Any element not listed here is appended as its own single step by
 * `buildSurveySteps`, so the survey never silently loses a question.
 */

/** Question group id of the WebK basic survey (see backend SURVEYS.BASIC_WEBK). */
const WEBK_QUESTION_GROUP_ID = 'must';

/** Shared anchor wording for every severity scale. */
const SEVERITY_MIN = { de: 'Gar nicht', en: 'Not at all' };
const SEVERITY_MAX = { de: 'Sehr stark', en: 'Very severe' };

/** Heading of every symptom card. */
const SYMPTOM_TITLE = { de: 'Aktuelle Beschwerden', en: 'Current symptoms' };

/** Intro screen — stays its own step in both presentations. */
const INTRO_STEP: SurveyStepConfig = { layout: 'single', elementIds: ['start'] };

/**
 * The two 0–10 burden questions and "how much could you eat". The 0–10
 * questions use a slider whose track carries the severity ramp — eleven
 * separate dots are too small to hit on a phone; the portion question uses pie
 * glyphs with shortened captions.
 */
const BURDEN_SECTION: SurveyStepConfig = {
  layout: 'stacked',
  elementIds: [
    'question-nutrition-problems',
    'question-stress-due-to-nutrition-problems',
    'question-eating-habits',
  ],
  display: {
    'question-nutrition-problems': {
      control: 'slider',
      minLabel: SEVERITY_MIN,
      maxLabel: SEVERITY_MAX,
    },
    'question-stress-due-to-nutrition-problems': {
      control: 'slider',
      minLabel: SEVERITY_MIN,
      maxLabel: SEVERITY_MAX,
    },
    'question-eating-habits': {
      control: 'portions',
      choiceLabels: {
        0: { de: 'So viel wie immer', en: 'As much as always' },
        1: { de: 'Etwas weniger', en: 'A little less' },
        2: { de: 'Etwa die Hälfte', en: 'About half' },
        3: { de: 'Weniger als die Hälfte', en: 'Less than half' },
        4: { de: 'Fast nichts mehr', en: 'Almost nothing' },
      },
    },
  },
};

/**
 * Symptoms of one body region. Every symptom is re-labelled to its short name
 * ("Leidest Du unter Appetitlosigkeit?" → "Appetitlosigkeit"), which also
 * shortens the long dumping-syndrome wording to "Dumpingsyndrom".
 */
const EATING_SECTION: SurveyStepConfig = {
  layout: 'symptoms',
  title: SYMPTOM_TITLE,
  subtitle: { de: 'Essen & Trinken', en: 'Eating & drinking' },
  icon: '🍽️',
  iconUrl: 'assets/images/SymptomEating.png',
  minLabel: SEVERITY_MIN,
  maxLabel: SEVERITY_MAX,
  elementIds: [
    'question-appetite-loss',
    'question-taste-changes',
    'question-chewing-swallowing',
    'question-oral-inflammation',
    'question-low-thirst',
    'question-salivation-changes',
  ],
  display: {
    'question-appetite-loss': { label: { de: 'Appetitlosigkeit', en: 'Lack of appetite' } },
    'question-taste-changes': { label: { de: 'Geschmacksveränderungen', en: 'Taste changes' } },
    'question-chewing-swallowing': {
      label: { de: 'Kau- und Schluckbeschwerden', en: 'Chewing and swallowing difficulties' },
    },
    'question-oral-inflammation': {
      label: { de: 'Mund- und Schleimhautentzündungen', en: 'Oral mucosal inflammation' },
    },
    'question-low-thirst': { label: { de: 'Geringes Durstgefühl', en: 'Low thirst' } },
    'question-salivation-changes': {
      label: { de: 'Veränderung des Speichelflusses', en: 'Changes in salivation' },
    },
  },
};

const STOMACH_SECTION: SurveyStepConfig = {
  layout: 'symptoms',
  title: SYMPTOM_TITLE,
  subtitle: { de: 'Magen', en: 'Stomach' },
  icon: '🤢',
  iconUrl: 'assets/images/SymptomStomach.png',
  minLabel: SEVERITY_MIN,
  maxLabel: SEVERITY_MAX,
  elementIds: [
    'question-nausea-vomiting',
    'question-feeling-of-fullness',
    'question-acid-indigestion',
    'question-dumping-syndrome',
  ],
  display: {
    'question-nausea-vomiting': {
      label: { de: 'Übelkeit und Erbrechen', en: 'Nausea and vomiting' },
    },
    'question-feeling-of-fullness': { label: { de: 'Völlegefühl', en: 'Feeling of fullness' } },
    'question-acid-indigestion': { label: { de: 'Sodbrennen', en: 'Acid indigestion' } },
    // Replaces the long wording incl. the symptom description in brackets.
    'question-dumping-syndrome': { label: { de: 'Dumpingsyndrom', en: 'Dumping syndrome' } },
  },
};

const GUT_SECTION: SurveyStepConfig = {
  layout: 'symptoms',
  title: SYMPTOM_TITLE,
  subtitle: { de: 'Darm und Verdauung', en: 'Gut and digestion' },
  icon: '🚽',
  iconUrl: 'assets/images/SymptomIntestine.png',
  minLabel: SEVERITY_MIN,
  maxLabel: SEVERITY_MAX,
  elementIds: [
    'question-abdominal-cramps',
    'question-bloating',
    'question-diarrhea',
    'question-constipation',
    'question-fatty-stool',
  ],
  display: {
    'question-abdominal-cramps': { label: { de: 'Bauchkrämpfe', en: 'Abdominal cramps' } },
    'question-bloating': { label: { de: 'Blähungen', en: 'Bloating' } },
    'question-diarrhea': { label: { de: 'Durchfall', en: 'Diarrhea' } },
    'question-constipation': { label: { de: 'Verstopfung', en: 'Constipation' } },
    'question-fatty-stool': { label: { de: 'Fettstuhl', en: 'Fatty stool' } },
  },
};

const SECTIONS = [BURDEN_SECTION, EATING_SECTION, STOMACH_SECTION, GUT_SECTION];

/** Stepped presentation: intro, then one screen per section. */
export const WEBK_SURVEY_LAYOUT: SurveyLayoutConfig = {
  [WEBK_QUESTION_GROUP_ID]: [INTRO_STEP, ...SECTIONS],
};

/**
 * One-page presentation: intro, then every section on a single scrollable
 * screen. The header counts answered questions instead of steps and the
 * stepper shows a "keep scrolling" cue while content is below the fold.
 */
export const WEBK_SURVEY_LAYOUT_ONE_PAGE: SurveyLayoutConfig = {
  [WEBK_QUESTION_GROUP_ID]: [
    INTRO_STEP,
    {
      layout: 'page',
      title: { de: 'Deine Ernährungssituation', en: 'Your nutritional situation' },
      subtitle: {
        de: 'Beantworte alle Fragen – scrolle dafür nach unten.',
        en: 'Answer every question — scroll down for all of them.',
      },
      sections: SECTIONS,
    },
  ],
};
